import { BRAND } from '../config/brand'
/**
 * Global Search Service
 * Handles searching across user data and help documentation
 * Uses client-side indexing for fast, instant search results
 */

// Help documentation entries
export interface HelpEntry {
  id: string
  title: string
  description: string
  category: 'getting-started' | 'features' | 'hierarchy' | 'ai' | 'scheduling' | 'settings'
  path?: string
  keywords: string[]
}

// Search result types
export interface UserDataResult {
  id: number
  title: string
  description?: string
  type: 'dream' | 'aspiration' | 'goal' | 'task' | 'action_item' | 'roadblock'
  status?: string
  priority?: string
  category?: string
  progress?: number
  target_date?: string
  due_date?: string
}

export interface SearchResults {
  userData: {
    dreams: UserDataResult[]
    aspirations: UserDataResult[]
    goals: UserDataResult[]
    skills: UserDataResult[]
    habits: UserDataResult[]
    tasks: UserDataResult[]
    action_items: UserDataResult[]
    roadblocks: UserDataResult[]
  }
  helpDocs: HelpEntry[]
  totalCount: number
}

// Indexed data store
interface SearchIndex {
  dreams: UserDataResult[]
  aspirations: UserDataResult[]
  goals: UserDataResult[]
  skills: UserDataResult[]
  habits: UserDataResult[]
  tasks: UserDataResult[]
  action_items: UserDataResult[]
  roadblocks: UserDataResult[]
  lastUpdated: number
}

// Built-in help documentation index
const helpDocumentation: HelpEntry[] = [
  // Getting Started
  {
    id: 'help-dashboard',
    title: 'Dashboard Overview',
    description: 'Your central hub showing progress summary, upcoming tasks, and quick actions',
    category: 'getting-started',
    path: '/dashboard',
    keywords: ['home', 'overview', 'summary', 'start', 'main', 'landing']
  },
  {
    id: 'help-profile',
    title: 'Profile Settings',
    description: 'Manage your account, preferences, theme, and personal information',
    category: 'settings',
    path: '/profile',
    keywords: ['account', 'settings', 'preferences', 'theme', 'dark mode', 'light mode', 'password', 'email']
  },
  {
    id: 'help-scheduling-prefs',
    title: 'Scheduling Preferences',
    description: 'Set your sleep schedule, energy levels, and activity preferences for optimal scheduling',
    category: 'scheduling',
    path: '/profile?tab=scheduling',
    keywords: ['sleep', 'wake', 'energy', 'morning', 'evening', 'schedule', 'productivity', 'focus']
  },
  
  // Hierarchy System
  {
    id: 'help-dreams',
    title: 'Dreams',
    description: 'Your biggest life visions and long-term aspirations. Dreams are at the top of your hierarchy.',
    category: 'hierarchy',
    path: '/dreams',
    keywords: ['vision', 'purpose', 'life goals', 'long term', 'big picture', 'create dream']
  },
  {
    id: 'help-goals',
    title: 'Goals',
    description: 'Specific, measurable objectives under aspirations. Goals have clear timelines and success criteria.',
    category: 'hierarchy',
    path: '/goals',
    keywords: ['objective', 'target', 'measurable', 'smart', 'specific', 'timeline']
  },
  {
    id: 'help-tasks',
    title: 'Tasks',
    description: 'Actionable work items that help you achieve your goals. Tasks can be scheduled and tracked.',
    category: 'hierarchy',
    path: '/tasks',
    keywords: ['todo', 'to do', 'work', 'action', 'complete', 'do']
  },
  {
    id: 'help-hierarchy-view',
    title: 'Life Map',
    description: 'Unified view of all your hierarchy items. See the full graph of dreams, aspirations, goals, tasks and how they connect.',
    category: 'hierarchy',
    path: '/life-map',
    keywords: ['tree', 'overview', 'all items', 'structure', 'organization', 'parent', 'child', 'life map', 'graph', 'my hierarchy']
  },
  {
    id: 'help-my-world',
    title: 'My World',
    description: `The people and places ${BRAND.assistant} knows — managed under the My World group in the nav (Life Map, My People, My Places).`,
    category: 'hierarchy',
    path: '/my-people',
    keywords: ['world', 'my world', 'overview', 'people', 'places', 'who', 'where', 'relationships', 'context']
  },
  {
    id: 'help-my-people',
    title: 'My People',
    description: `The people and relationships ${BRAND.assistant} remembers — add yourself and the people in your life so ${BRAND.assistant} knows who you mean across conversations.`,
    category: 'hierarchy',
    path: '/my-people',
    keywords: ['people', 'my people', 'person', 'relationships', 'family', 'friends', 'contacts', 'who', 'identity', 'about you']
  },
  {
    id: 'help-my-places',
    title: 'My Places',
    description: `Save the spots where your tasks live — home, work, the grocery store — so ${BRAND.assistant} can remind you the moment you walk in.`,
    category: 'hierarchy',
    path: '/places',
    keywords: ['places', 'my places', 'location', 'geofence', 'where', 'home', 'work', 'address', 'map', 'reminder']
  },

  // AI Features
  {
    id: 'help-horton-ai',
    title: `Chat with ${BRAND.assistant}`,
    description: `Have a conversation with ${BRAND.assistant}, your AI productivity assistant. Get help planning, organizing, and achieving your goals.`,
    category: 'ai',
    path: '/horton-ai',
    keywords: ['chat', 'ai', 'assistant', 'help', 'horton', 'conversation', 'planning', 'advice']
  },
  {
    id: 'help-ai-suggestions',
    title: 'AI Suggestions',
    description: `${BRAND.assistant} can analyze your goals and suggest tasks, identify patterns, and provide personalized recommendations.`,
    category: 'ai',
    keywords: ['suggestions', 'recommendations', 'analysis', 'patterns', 'insights', 'smart']
  },
  
  // Scheduling
  {
    id: 'help-schedule',
    title: 'Schedule View',
    description: 'View and manage your scheduled tasks and action items. See what\'s coming up and plan your time.',
    category: 'scheduling',
    path: '/schedule',
    keywords: ['calendar', 'schedule', 'upcoming', 'plan', 'time', 'when', 'date']
  },
  {
    id: 'help-today-focus',
    title: "Today's Focus",
    description: 'See what you should focus on today. AI-curated list of priority items for maximum productivity.',
    category: 'scheduling',
    path: '/today',
    keywords: ['today', 'focus', 'priority', 'now', 'current', 'daily', 'routine']
  },
  
  // Features
  {
    id: 'help-progress-tracking',
    title: 'Progress Tracking',
    description: 'Track your progress on goals and tasks. See completion rates, streaks, and analytics.',
    category: 'features',
    keywords: ['progress', 'track', 'complete', 'percentage', 'analytics', 'stats', 'streak']
  },
  {
    id: 'help-recurring-tasks',
    title: 'Recurring Tasks',
    description: 'Set up tasks that repeat daily, weekly, or on custom schedules. Build habits with recurring items.',
    category: 'features',
    keywords: ['recurring', 'repeat', 'daily', 'weekly', 'monthly', 'habit', 'routine', 'regular']
  },
  {
    id: 'help-priorities',
    title: 'Priority Levels',
    description: 'Set priorities (low, medium, high, critical) on items to focus on what matters most.',
    category: 'features',
    keywords: ['priority', 'important', 'urgent', 'critical', 'high', 'low', 'medium', 'rank']
  },
  {
    id: 'help-keyboard-shortcuts',
    title: 'Keyboard Shortcuts',
    description: 'Use ⌘K or Ctrl+K to open search. Press Escape to close dialogs. Navigate efficiently with shortcuts.',
    category: 'features',
    keywords: ['keyboard', 'shortcut', 'hotkey', 'cmd', 'ctrl', 'escape', 'fast', 'quick']
  },
  {
    id: 'help-theme',
    title: 'Theme Settings',
    description: 'Switch between light and dark mode. Your preference syncs across devices.',
    category: 'settings',
    path: '/profile',
    keywords: ['theme', 'dark mode', 'light mode', 'appearance', 'color', 'night']
  },
  {
    id: 'help-create-item',
    title: 'Creating Items',
    description: `Create new dreams, goals, tasks, and more. Use the + button on any list page, or ask ${BRAND.assistant}.`,
    category: 'getting-started',
    keywords: ['create', 'new', 'add', 'make', 'plus', 'button']
  },
  {
    id: 'help-edit-item',
    title: 'Editing Items',
    description: 'Click on any item to view and edit its details. Update status, progress, and other properties.',
    category: 'getting-started',
    keywords: ['edit', 'update', 'change', 'modify', 'details']
  },
  {
    id: 'help-delete-item',
    title: 'Deleting Items',
    description: 'Delete items from the detail view. Be careful - deleting parent items may affect children.',
    category: 'getting-started',
    keywords: ['delete', 'remove', 'trash', 'discard', 'clear']
  }
]

class SearchService {
  private baseUrl = '/api/hierarchy'
  private index: SearchIndex | null = null
  private indexPromise: Promise<SearchIndex> | null = null
  private readonly INDEX_TTL = 5 * 60 * 1000 // 5 minutes cache
  
  /**
   * Build or refresh the search index by fetching all user data
   */
  async buildIndex(force: boolean = false): Promise<SearchIndex> {
    // Return cached index if still valid
    if (!force && this.index && (Date.now() - this.index.lastUpdated < this.INDEX_TTL)) {
      return this.index
    }
    
    // If already fetching, return the existing promise
    if (this.indexPromise) {
      return this.indexPromise
    }
    
    // Build new index
    this.indexPromise = this.fetchAllUserData()
    
    try {
      this.index = await this.indexPromise
      return this.index
    } finally {
      this.indexPromise = null
    }
  }
  
  /**
   * Fetch all user data from individual endpoints
   */
  private async fetchAllUserData(): Promise<SearchIndex> {
    const endpoints = [
      { key: 'dreams', url: `${this.baseUrl}/dreams` },
      { key: 'goals', url: `${this.baseUrl}/goals` },
      { key: 'skills', url: `${this.baseUrl}/skills` },
      { key: 'habits', url: `${this.baseUrl}/habits` },
      { key: 'tasks', url: `${this.baseUrl}/tasks` },
    ]

    const index: SearchIndex = {
      dreams: [],
      aspirations: [],
      goals: [],
      skills: [],
      habits: [],
      tasks: [],
      action_items: [],
      roadblocks: [],
      lastUpdated: Date.now()
    }
    
    // Fetch all endpoints in parallel
    const results = await Promise.allSettled(
      endpoints.map(async ({ key, url }) => {
        try {
          const response = await fetch(url, {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          })
          
          if (!response.ok) {
            console.warn(`Failed to fetch ${key}: ${response.status}`)
            return { key, data: [] }
          }
          
          const data = await response.json()
          // Handle both array responses and { data: [] } responses
          const items = Array.isArray(data) ? data : (data.data || [])
          return { key, data: items }
        } catch (error) {
          console.warn(`Error fetching ${key}:`, error)
          return { key, data: [] }
        }
      })
    )
    
    // Process results
    for (const result of results) {
      if (result.status === 'fulfilled') {
        const { key, data } = result.value
        index[key as keyof Omit<SearchIndex, 'lastUpdated'>] = data.map((item: any) => ({
          id: item.id,
          title: item.title || '',
          description: item.description || '',
          type: key.replace('_', '-') as UserDataResult['type'],
          status: item.status,
          priority: item.priority,
          category: item.category,
          progress: item.progress,
          target_date: item.target_date,
          due_date: item.due_date
        }))
      }
    }
    
    console.log('🔍 Search index built:', {
      dreams: index.dreams.length,
      aspirations: index.aspirations.length,
      goals: index.goals.length,
      skills: index.skills.length,
      habits: index.habits.length,
      tasks: index.tasks.length,
      action_items: index.action_items.length,
      roadblocks: index.roadblocks.length
    })
    
    return index
  }
  
  /**
   * Invalidate the current index (call after creating/updating/deleting items)
   */
  invalidateIndex(): void {
    this.index = null
  }
  
  /**
   * Search user data using the local index
   */
  private searchIndexedData(query: string, index: SearchIndex): SearchResults['userData'] {
    const normalizedQuery = query.toLowerCase().trim()
    const words = normalizedQuery.split(/\s+/).filter(w => w.length > 0)
    
    const matchItem = (item: UserDataResult): boolean => {
      const title = (item.title || '').toLowerCase()
      const description = (item.description || '').toLowerCase()
      const category = (item.category || '').toLowerCase()
      const status = (item.status || '').toLowerCase()
      
      // Check if query matches title or description
      if (title.includes(normalizedQuery) || description.includes(normalizedQuery)) {
        return true
      }
      
      // Check category and status
      if (category.includes(normalizedQuery) || status.includes(normalizedQuery)) {
        return true
      }
      
      // Check if all words match somewhere
      return words.every(word => 
        title.includes(word) || 
        description.includes(word) ||
        category.includes(word) ||
        status.includes(word)
      )
    }
    
    return {
      dreams: index.dreams.filter(matchItem).slice(0, 10),
      aspirations: index.aspirations.filter(matchItem).slice(0, 10),
      goals: index.goals.filter(matchItem).slice(0, 10),
      skills: index.skills.filter(matchItem).slice(0, 10),
      habits: index.habits.filter(matchItem).slice(0, 10),
      tasks: index.tasks.filter(matchItem).slice(0, 10),
      action_items: index.action_items.filter(matchItem).slice(0, 10),
      roadblocks: index.roadblocks.filter(matchItem).slice(0, 10)
    }
  }
  
  /**
   * Search help documentation
   */
  searchHelpDocs(query: string): HelpEntry[] {
    if (!query || query.length < 2) return []
    
    const normalizedQuery = query.toLowerCase().trim()
    const words = normalizedQuery.split(/\s+/)
    
    return helpDocumentation.filter(entry => {
      // Check title
      if (entry.title.toLowerCase().includes(normalizedQuery)) return true
      
      // Check description
      if (entry.description.toLowerCase().includes(normalizedQuery)) return true
      
      // Check keywords
      const keywordMatch = entry.keywords.some(keyword => 
        keyword.toLowerCase().includes(normalizedQuery) ||
        normalizedQuery.includes(keyword.toLowerCase())
      )
      if (keywordMatch) return true
      
      // Check if all words match somewhere
      const allWordsMatch = words.every(word => 
        entry.title.toLowerCase().includes(word) ||
        entry.description.toLowerCase().includes(word) ||
        entry.keywords.some(k => k.toLowerCase().includes(word))
      )
      if (allWordsMatch) return true
      
      return false
    }).slice(0, 10) // Limit to 10 results
  }
  
  /**
   * Combined search across all sources
   */
  async search(query: string): Promise<SearchResults> {
    if (!query || query.length < 2) {
      return {
        userData: this.getEmptyUserData(),
        helpDocs: [],
        totalCount: 0
      }
    }
    
    // Build/get index
    const index = await this.buildIndex()
    
    // Search help docs (local)
    const helpDocs = this.searchHelpDocs(query)
    
    // Search user data using index
    const userData = this.searchIndexedData(query, index)
    
    // Calculate total count
    const userDataCount =
      userData.dreams.length +
      userData.aspirations.length +
      userData.goals.length +
      userData.skills.length +
      userData.habits.length +
      userData.tasks.length +
      userData.action_items.length +
      userData.roadblocks.length
    
    return {
      userData,
      helpDocs,
      totalCount: userDataCount + helpDocs.length
    }
  }
  
  /**
   * Preload the search index (call on app load or when opening search)
   */
  async preloadIndex(): Promise<void> {
    try {
      await this.buildIndex()
    } catch (error) {
      console.warn('Failed to preload search index:', error)
    }
  }
  
  /**
   * Get all help documentation for browsing
   */
  getAllHelpDocs(): HelpEntry[] {
    return helpDocumentation
  }
  
  /**
   * Get help docs by category
   */
  getHelpDocsByCategory(category: HelpEntry['category']): HelpEntry[] {
    return helpDocumentation.filter(entry => entry.category === category)
  }
  
  private getEmptyUserData(): SearchResults['userData'] {
    return {
      dreams: [],
      aspirations: [],
      goals: [],
      skills: [],
      habits: [],
      tasks: [],
      action_items: [],
      roadblocks: []
    }
  }
}

export const searchService = new SearchService()

